// スキル（スキルツリーのページ）：キャラ別ツリー＋共通の身体・戦術・建築。解放力の範囲で装着、いつでも無料で組み替え。
// 編成画面の「スキル」から開く（#/skills?c=キャラ）。ツリーは枠の中だけ動かし、画面は1画面に収める。
// 上は1行（戻る・キャラ・解放力・おすすめ・保存枠）、残りは全部ツリー。丸を押すと詳細がツリーの上に出る。
import {
  CHARS,
  CHAR_IDS,
  SKILLS,
  SKILL_BY_ID,
  GRAPH_CORE,
  unlockLvFor,
  skillGraph,
  skillLadder,
  skillLines,
  lineLevels,
  lineOfWaza,
  lineStep,
  linePointsOf,
  buildLoadout,
  type BattleLoadout,
  WAZA_LVS,
  lineCost,
  LINE_MAX,
  LINE_JUTSU_LV,
  LINE_OUGI_LV,
  jutsuView,
  ladderAt,
  ladderLinked,
  ladderOpen,
  ladderX,
  ladderChanges,
  ladderPick,
  statLevel,
  STAT_STEP,
  effectText,
  SKILL_GROUPS,
  SKILL_GROUP_LABEL,
  canEquipSkill,
  dependentsOf,
  equippedSkills,
  JUTSU_BY_ID,
  selectedJutsu,
  presetSlots,
  skillCapacity,
  skillCost,
  type CharId,
  type Profile,
  type SkillGroup,
  type SkillNode,
  META_CONFIG,
} from '@hyakki/core';
import { api } from '../api';
import { go, register } from '../router';
import { state, subscribe } from '../state';
import { markSkillsSeen, seenLevel } from '../ui/skill-news';
import { esc } from '../ui/dom';
import { onSwipe, stepChar } from '../ui/fit';
import { icon, nodeIcon, type IconName } from '../ui/icons';
import { confirmDialog, openModal, toast } from '../ui/overlay';
import { notReadyHtml, runOp as run } from '../ui/status';
import { rem } from '../ui/viewport';
import { faceStyle } from '../assets';
import '../styles/screens/skilltree.css';
import { setPointsChar, setSubTab } from '../ui/shell';

export const GROUP_ICON: Record<SkillGroup, string> = { char: 'mon', body: 'heart', tactics: 'sortie', build: 'village' };

let charId: CharId = 'akari';
const sel = { skill: '' as string, group: 'char' as SkillGroup };

register('skills', {
  title: 'スキル',
  nav: 'chara',
  mount(el, params) {
    const c = params.get('c') as CharId | null;
    charId = c && CHAR_IDS.includes(c) ? c : state.oshi;
    // 編成の術の札から：覚えるスキルを開いた状態で
    const want = params.get('s');
    if (want && SKILL_BY_ID[want]) {
      sel.group = 'char';
      sel.skill = want;
    }
    newSince = seenLevel(state.profile?.level ?? 1);
    // 系統のキャラは編成の「スキル」タブで組む
    if (skillLines(charId)) {
      queueMicrotask(() => go('chara', { c: charId, tab: 'skill' }));
      return () => {};
    }
    el.innerHTML = `<div class="page fill enhance"><section class="panel-paper deco st-panel"></section></div>`;
    const body = el.querySelector<HTMLElement>('.st-panel')!;
    const draw = () => {
      const p = state.profile;
      setPointsChar(charId);
      if (!p) return void (body.innerHTML = notReadyHtml('育成データ'));
      drawSkills(body, p);
    };
    draw();
    const off = subscribe((ch) => {
      if (ch.has('profile')) draw();
    });
    const handler = makeHandler(() => draw());
    el.addEventListener('click', handler);
    const offSwipe = onSwipe(el, '[data-swipe-char]', (d) => {
      charId = stepChar(charId, d);
      sel.skill = '';
      draw();
    });
    return () => {
      off();
      offSwipe();
      el.removeEventListener('click', handler);
    };
  },
});

function makeHandler(redraw: () => void) {
  return async (e: Event) => {
    const t = e.target as HTMLElement;
    const p = state.profile;
    if (!p) return;
    if (t.closest('[data-back]')) return go('chara', { c: charId });
    const btn = t.closest<HTMLButtonElement>('button');
    if (btn?.disabled) return;
    const step = t.closest<HTMLElement>('[data-charstep]');
    if (step) {
      charId = stepChar(charId, Number(step.dataset.charstep));
      sel.skill = '';
      return redraw();
    }
    const grp = t.closest<HTMLElement>('[data-sgroup]');
    if (grp) {
      sel.group = grp.dataset.sgroup as SkillGroup;
      sel.skill = '';
      return redraw();
    }
    const node = t.closest<HTMLElement>('[data-skill]');
    if (node) {
      // 系統のキャラの術：小窓で詳細とセット
      if (node.dataset.skill!.startsWith('@jutsu:')) return void jutsuDialog(p, node.dataset.skill!.slice(7));
      sel.skill = node.dataset.skill!;
      return redraw();
    }
    // 詳細を閉じる：×か、ツリーの何もないところを押す
    if (t.closest('[data-stclose]') || (sel.skill && t.closest('.st-view') && !t.closest('.st-pop, .st-zoom, [data-a]'))) {
      sel.skill = '';
      return redraw();
    }
    const a = t.closest<HTMLElement>('[data-a]')?.dataset.a;
    if (!a) return;
    if (btn) btn.disabled = true;
    switch (a) {
      case 'skon':
      case 'skoff':
        await toggleSkill(p, a === 'skon');
        break;
      case 'skauto':
        await autoSkills(p, charId);
        break;
      case 'skpreset':
        presetDialog(p);
        break;
      case 'skpick':
        await pickLadder(p);
        break;
      case 'lnup':
      case 'lndown': {
        const i = Number(t.closest<HTMLElement>('[data-line]')?.dataset.line);
        lastLine = i;
        sel.skill = `@line:${i}`;
        const next = lineStep(p, charId, equippedSkills(p, charId), i, a === 'lnup' ? 1 : -1);
        if (next) await run(api.skillsSet(charId, next));
        else if (a === 'lnup') toast('ポイントが足りません');
        break;
      }
      case 'lnjutsu': {
        const j = t.closest<HTMLElement>('[data-jutsu]')?.dataset.jutsu;
        if (j) await run(api.skillsJutsu(charId, j));
        break;
      }
    }
    if (btn?.isConnected) btn.disabled = false;
  };
}

/** おすすめ構成に組み直す（手動の構成なら確認してから） */
export async function autoSkills(p: Profile, c: CharId) {
  const manual = p.skills?.[c] && !p.skills[c].auto;
  if (manual && !(await confirmDialog(`${CHARS[c].name}のスキルをおすすめ構成に組み直します。\n今の構成は上書きされます（費用はかかりません）。\n以後は主Lvが上がるたびに自動で組み直します。`, { ok: '組み直す' }))) return;
  await run(api.skillsAuto(c), 'おすすめ構成にしました', 'unlock');
}

/** スキル画面を開いた時点で「見た」ことになっていた主Lv（これより後に解放されたものに「新」） */
let newSince = 1;

const FLAG_ICON: [string, string][] = [
  ['surge:', 'star'],
  ['momentum', 'move'],
  ['predator', 'oni'],
  ['conqueror', 'flag'],
  ['regen', 'heart'],
  ['sanctuary', 'shield'],
  ['siege', 'castle'],
  ['riposte', 'guard'],
  ['execution', 'attack'],
  ['bu_counter', 'counter'],
  ['kaeshi', 'counter'],
  ['stride', 'move'],
  ['hitrun', 'move'],
  ['duelist', 'attack'],
  ['fushin_endure', 'village'],
  ['fushin_guard', 'shield'],
  ['fushin_repair', 'village'],
  ['ougi:akari_lantern', 'flag'],
  ['ougi:kazamaru_senpu', 'skill'],
  ['ougi:yuzuha_kekkai', 'burst'],
];
export function skillIcon(s: SkillNode) {
  for (const [k, ic] of FLAG_ICON) if (s.flags.some((f) => f.startsWith(k))) return ic;
  return nodeIcon(s.effects[0]?.key);
}

/** 装着できない理由（できるなら null） */
function blockReason(p: Profile, s: SkillNode, cur: string[]): string | null {
  const r = canEquipSkill(p, charId, cur, s.id);
  if (r.ok) return null;
  switch (r.error) {
    case 'locked':
      return `主Lv${unlockLvFor(charId, s)}で解放`;
    case 'requires':
      if (skillGraph(charId)?.pos[s.id]) return '取ったマスから線でつながるマスだけ取れます';
      return `先に「${s.requires.filter((x) => !cur.includes(x)).map((x) => SKILL_BY_ID[x]?.name ?? x).join('」「')}」を装着`;
    case 'capacity':
      return `ポイントが足りません（あと${skillCost(cur) + s.cost - skillCapacity(p).total}）`;
    default:
      return 'このスキルは装着できません';
  }
}

// ---------- ツリー（中心にキャラ、4本の幹：上＝専用・右＝身体・下＝戦術・左＝建築） ----------
// 各系統（同じ列の段の連なり）は幹から直角に枝分かれし、外へ段の順に伸びる。どの幹の枝も同じ側へ出す（風車の形）ので、
// 別の幹の枝が交わらない。座標は「マス」（U px）。枠の中をドラッグで動かし、ホイール・ピンチで拡大縮小する。
const U = 64;
/** 幹の向き（d）と枝の向き（b）。風車の形：上の幹の枝は右、右の幹の枝は下、下は左、左は上 */
const ARM: Record<SkillGroup, { d: [number, number]; b: [number, number] }> = {
  char: { d: [0, -1], b: [1, 0] },
  body: { d: [1, 0], b: [0, 1] },
  tactics: { d: [0, 1], b: [-1, 0] },
  build: { d: [-1, 0], b: [0, -1] },
};
/** 幹の最初の分かれ目までの長さ・分かれ目の間隔・枝の最初の丸までの長さ・丸の間隔（マス） */
const TREE = { first: 1.9, gap: 1.55, lead: 1.35, step: 1.25 };
/** 専用の幹は、術を覚える大きな丸（菱形）があるので分かれ目の間隔を広く */
const CHAR_GAP = 3;
/** 奥義の冠：専用の幹の先から fork マス上で分かれ、rise マス上に咲く（左右は spread マス、外側ほど sag マス下がる）。title は見出しの高さ */
const OUGI_CROWN = { fork: 0.9, rise: 2.1, spread: 2.3, sag: 0.45, title: 1.25 };
/** 枠の中の見え方（キャラごと）。描き直しても保つ */
const camera: Record<string, { x: number; y: number; z: number }> = {};

interface TreeLayout {
  nodes: { s: SkillNode; x: number; y: number }[];
  /** 幹の分かれ目（系統ごと） */
  joints: { g: SkillGroup; line: string; x: number; y: number; first: string }[];
  /** 幹の先端（種類ごと） */
  tips: Record<SkillGroup, { x: number; y: number }>;
  /** 幹の先に突き出るスキル（専用の奥義）。幹はここまで伸ばす */
  apex: { x: number; y: number } | null;
  /** 奥義の冠（分かれ目と見出しの位置） */
  crown: { fork: { x: number; y: number }; title: { x: number; y: number } } | null;
  bounds: { x0: number; y0: number; x1: number; y1: number };
}
/** 網のキャラ（skill-graph）：格子の座標をそのまま置く。1マス＝GRID 単位 */
const GRID = 0.72;
function graphLayout(c: CharId): TreeLayout | null {
  const g = skillGraph(c);
  if (!g) return null;
  const nodes = Object.entries(g.pos)
    .filter(([id]) => id !== GRAPH_CORE && SKILL_BY_ID[id])
    .map(([id, [x, y]]) => ({ s: SKILL_BY_ID[id], x: x * GRID, y: y * GRID }));
  const xs = [0, ...nodes.map((n) => n.x)];
  const ys = [0, ...nodes.map((n) => n.y)];
  const zero = { x: 0, y: 0 };
  return { nodes, joints: [], tips: { char: zero, body: zero, tactics: zero, build: zero }, apex: null, crown: null, bounds: { x0: Math.min(...xs) - 1.2, y0: Math.min(...ys) - 1.2, x1: Math.max(...xs) + 1.2, y1: Math.max(...ys) + 1.4 } };
}
function treeLayout(c: CharId): TreeLayout {
  const graph = graphLayout(c);
  if (graph) return graph;
  const nodes: TreeLayout['nodes'] = [];
  const joints: TreeLayout['joints'] = [];
  const tips = {} as TreeLayout['tips'];
  let apex: TreeLayout['apex'] = null;
  let crown: TreeLayout['crown'] = null;
  for (const g of SKILL_GROUPS) {
    const all = SKILLS.filter((s) => (g === 'char' ? s.charId === c : s.group === g && s.charId === null));
    const list = all.filter((s) => !s.apex);
    const lines = [...new Set(list.map((s) => s.col))].sort((a, b) => a - b);
    const { d, b } = ARM[g];
    lines.forEach((col, k) => {
      const dist = TREE.first + k * (g === 'char' ? CHAR_GAP : TREE.gap);
      const jx = d[0] * dist;
      const jy = d[1] * dist;
      const isOugi = (s: SkillNode) => s.flags.some((f) => f.startsWith('jougi:'));
      const chain = list.filter((s) => s.col === col && !isOugi(s)).sort((a, z) => a.tier - z.tier);
      joints.push({ g, line: chain[0]?.line ?? '', x: jx, y: jy, first: chain[0]?.id ?? '' });
      chain.forEach((s, t) => nodes.push({ s, x: jx + b[0] * (TREE.lead + t * TREE.step), y: jy + b[1] * (TREE.lead + t * TREE.step) }));
    });
    const end = TREE.first + Math.max(0, lines.length - 1) * (g === 'char' ? CHAR_GAP : TREE.gap) + 0.9;
    tips[g] = { x: d[0] * end, y: d[1] * end };
    // 奥義：専用の幹の先（名前の札のさらに上）で3つに分かれて咲く。左・中・右（系統の順）
    const ougis = list.filter((s) => s.flags.some((f) => f.startsWith('jougi:'))).sort((a, z) => a.col - z.col);
    if (g === 'char' && ougis.length) {
      const fy = -(end + OUGI_CROWN.fork);
      crown = { fork: { x: 0, y: fy }, title: { x: 0, y: fy - OUGI_CROWN.rise - OUGI_CROWN.title } };
      const n = ougis.length;
      ougis.forEach((og, i) => {
        const k = n === 1 ? 0 : (i / (n - 1)) * 2 - 1; // -1 … 1
        nodes.push({ s: og, x: k * OUGI_CROWN.spread, y: fy - OUGI_CROWN.rise + Math.abs(k) * OUGI_CROWN.sag });
      });
    }
    // 幹の先に突き出る1つ（専用の奥義）：種類の名前の札のさらに先
    const top = all.find((s) => s.apex);
    if (top) {
      apex = { x: d[0] * (end + 1.25), y: d[1] * (end + 1.25) };
      nodes.push({ s: top, ...apex });
    }
  }
  const xs = [...nodes.map((n) => n.x), ...Object.values(tips).map((t) => t.x), 0];
  const ys = [...nodes.map((n) => n.y), ...Object.values(tips).map((t) => t.y), 0, ...(crown ? [crown.title.y - 0.3] : [])];
  return { nodes, joints, tips, apex, crown, bounds: { x0: Math.min(...xs) - 1.2, y0: Math.min(...ys) - 1.2, x1: Math.max(...xs) + 1.2, y1: Math.max(...ys) + 1.4 } };
}

function drawSkills(body: HTMLElement, p: Profile) {
  markSkillsSeen(p.level);
  setSubTab('chara', 'skill');
  if (skillLines(charId)) return drawLines(body, p);
  if (skillLadder(charId)) return drawLadder(body, p);
  const cur = equippedSkills(p, charId);
  const on = new Set(cur);
  const cap = skillCapacity(p).total;
  const used = skillCost(cur);
  const auto = p.skills?.[charId]?.auto !== false;
  const L = treeLayout(charId);
  if (!L.nodes.some((n) => n.s.id === sel.skill)) sel.skill = '';
  const { x0, y0, x1, y1 } = L.bounds;
  const W = (x1 - x0) * U;
  const H = (y1 - y0) * U;
  const px = (x: number) => (x - x0) * U;
  const py = (y: number) => (y - y0) * U;
  const at = new Map(L.nodes.map((n) => [n.s.id, n]));
  // 線：中心→幹の分かれ目→…（その種類で装着しているいちばん先の分かれ目までは金）、分かれ目→枝の最初、枝の中は前提どおり
  const groupLit: Record<string, number> = {};
  for (const j of L.joints) if (L.nodes.some((n) => n.s.group === j.g && (j.g !== 'char' || n.s.charId === charId) && n.s.line === j.line && on.has(n.s.id))) groupLit[j.g] = Math.max(groupLit[j.g] ?? -1, L.joints.filter((x) => x.g === j.g).indexOf(j));
  const seg = (ax: number, ay: number, bx: number, by: number, cls: string) => `<line x1="${px(ax)}" y1="${py(ay)}" x2="${px(bx)}" y2="${py(by)}" class="${cls}"/>`;
  let lines = '';
  const G = skillGraph(charId);
  if (G) {
    // 網：線は直角に折る。折れ角が他のマスに重なるなら、もう一方の向きに折る
    const occ = new Set(L.nodes.map((n) => `${n.x.toFixed(2)},${n.y.toFixed(2)}`).concat(['0.00,0.00']));
    const posOf = (id: string) => (id === GRAPH_CORE ? { x: 0, y: 0 } : at.get(id));
    for (const [a, b] of G.edges) {
      const A = posOf(a);
      const B = posOf(b);
      if (!A || !B) continue;
      const onA = a === GRAPH_CORE || on.has(a);
      const onB = b === GRAPH_CORE || on.has(b);
      const cls = `branch ${onA && onB ? 'on' : onA || onB ? 'half' : ''}`;
      if (Math.abs(A.x - B.x) < 1e-6 || Math.abs(A.y - B.y) < 1e-6) {
        lines += seg(A.x, A.y, B.x, B.y, cls);
        continue;
      }
      const hFirst = !occ.has(`${B.x.toFixed(2)},${A.y.toFixed(2)}`);
      const mx = hFirst ? B.x : A.x;
      const my = hFirst ? A.y : B.y;
      lines += `<path d="M${px(A.x)} ${py(A.y)} L${px(mx)} ${py(my)} L${px(B.x)} ${py(B.y)}" class="${cls}"/>`;
      lines += `<circle cx="${px(mx)}" cy="${py(my)}" r="3" class="gjoint ${onA && onB ? 'on' : ''}"/>`;
    }
  }
  for (const g of SKILL_GROUPS) {
    if (G) break;
    const js = L.joints.filter((j) => j.g === g);
    let ax = 0;
    let ay = 0;
    js.forEach((j, k) => {
      lines += seg(ax, ay, j.x, j.y, `trunk ${k <= (groupLit[g] ?? -1) ? 'on' : ''}`);
      ax = j.x;
      ay = j.y;
    });
    // 幹の先の奥義を装着していれば、幹の先まで金
    const top = g === 'char' && L.apex ? L.nodes.find((n) => n.s.apex) : undefined;
    const apexOn = !!top && on.has(top.s.id);
    lines += seg(ax, ay, L.tips[g].x, L.tips[g].y, apexOn ? 'trunk on' : 'trunk tip');
    if (top && L.apex) lines += seg(L.tips[g].x, L.tips[g].y, L.apex.x, L.apex.y, apexOn ? 'trunk on' : 'trunk tip');
    // 奥義の冠へ：幹の先から分かれ目まで
    if (g === 'char' && L.crown) {
      const anyOn = L.nodes.some((n) => n.s.flags.some((f) => f.startsWith('jougi:')) && on.has(n.s.id));
      lines += seg(L.tips[g].x, L.tips[g].y, L.crown.fork.x, L.crown.fork.y, `trunk crown ${anyOn ? 'on' : ''}`);
    }
  }
  for (const j of L.joints) {
    const n = at.get(j.first);
    if (n) lines += seg(j.x, j.y, n.x, n.y, `branch ${on.has(j.first) ? 'on' : ''}`);
  }
  for (const n of L.nodes) {
    // 系統の先の奥義：線は系統の最後の丸から（前提は術を覚えるスキルだが、枝の先に続けて描く）
    if (n.s.flags.some((f) => f.startsWith('jougi:'))) {
      // 奥義の枝：冠の分かれ目から弧を描いて各奥義へ（覚えていれば金）
      const f = L.crown?.fork;
      if (f) {
        const mx = n.x;
        const my = f.y;
        lines += `<path d="M${px(f.x)} ${py(f.y)} Q${px(mx)} ${py(my)} ${px(n.x)} ${py(n.y)}" class="branch ougi ${on.has(n.s.id) ? 'on' : ''}"/>`;
      }
      continue;
    }
    if (G) continue;
    for (const r of n.s.requires) {
      const a = at.get(r);
      if (a) lines += seg(a.x, a.y, n.x, n.y, `branch ${on.has(r) && on.has(n.s.id) ? 'on' : on.has(r) ? 'half' : ''}`);
    }
  }
  const joints = L.joints
    .map((j) => `<i class="st-joint ${j.first && on.has(j.first) ? 'on' : ''}" style="left:${rem(px(j.x))};top:${rem(py(j.y))}"></i>`)
    .join('');
  // 系統の名前（分かれ目の枝の付け根の脇）・種類の名前（幹の先）
  const lineLabels = L.joints
    .map((j) => {
      const { b } = ARM[j.g];
      const lx = j.x + b[0] * 0.55 + (b[1] ? 0.5 : 0);
      const ly = j.y + b[1] * 0.55 - (b[0] ? 0.42 : 0);
      return `<span class="st-line ${b[1] ? 'v' : ''}" style="left:${rem(px(lx))};top:${rem(py(ly))}">${esc(j.line)}</span>`;
    })
    .join('');
  const groupLabels = G ? '' : SKILL_GROUPS.map((g) => {
    const t = L.tips[g];
    const name = g === 'char' ? `${CHARS[charId].name}専用` : SKILL_GROUP_LABEL[g];
    return `<span class="st-group g-${g}" style="left:${rem(px(t.x))};top:${rem(py(t.y))}">${icon(GROUP_ICON[g])}${esc(name)}</span>`;
  }).join('');
  const crownTitle = L.crown ? `<span class="st-crown" style="left:${rem(px(L.crown.title.x))};top:${rem(py(L.crown.title.y))}">奥義</span>` : '';
  const crownFork = L.crown ? `<i class="st-joint crown" style="left:${rem(px(L.crown.fork.x))};top:${rem(py(L.crown.fork.y))}"></i>` : '';
  const carried = selectedJutsu(p, charId);
  const nodeBtn = ({ s, x, y }: TreeLayout['nodes'][number]) => {
    const locked = p.level < unlockLvFor(charId, s);
    const isOn = on.has(s.id);
    const why = isOn ? null : blockReason(p, s, cur);
    const fresh = !locked && unlockLvFor(charId, s) > newSince && unlockLvFor(charId, s) > 1;
    const og = ougiOfNode(s);
    if (og) {
      // 系統の先の奥義：紅と金の大きな丸。持っていく術の奥義なら光る
      const mine = og.id === carried;
      return `<button class="st-node st-onode ${isOn ? 'got' : ''} ${locked ? 'locked' : why ? 'blocked' : 'open'} ${mine ? 'carried' : ''} ${sel.skill === s.id ? 'sel' : ''}"
        style="left:${rem(px(x))};top:${rem(py(y))}" data-skill="${esc(s.id)}" aria-pressed="${isOn}" aria-label="${esc(s.name)}・コスト${s.cost}${locked ? `・主Lv${unlockLvFor(charId, s)}で解放` : isOn ? '・覚えた' : ''}" title="${esc(s.name)}：${esc(s.desc)}">
        <span class="st-ring"><i class="st-oart" style="${jutsuArt(og.id)}"></i>${locked ? `<i class="st-lock">${icon('lock')}</i>` : ''}${fresh ? '<i class="st-new">新</i>' : ''}<em class="st-cost">${locked ? `Lv${unlockLvFor(charId, s)}` : s.cost}</em></span>
        <i class="st-otag">奥義</i><b>${esc(og.ougiName)}</b></button>`;
    }
    const j = jutsuOfNode(s);
    if (j) {
      // 術を覚える：ジョブチェンジの節目。大きな菱形の枠に術の絵、「術」の札と術の名前
      const mine = j.id === carried;
      return `<button class="st-node st-jnode ${isOn ? 'got' : ''} ${locked ? 'locked' : why ? 'blocked' : 'open'} ${mine ? 'carried' : ''} ${sel.skill === s.id ? 'sel' : ''}"
        style="left:${rem(px(x))};top:${rem(py(y))}" data-skill="${esc(s.id)}" aria-pressed="${isOn}" aria-label="術「${esc(j.name)}」を覚える・${esc(s.name)}・コスト${s.cost}${locked ? `・主Lv${unlockLvFor(charId, s)}で解放` : isOn ? '・覚えた' : ''}" title="術「${esc(j.name)}」：${esc(j.desc)}">
        <span class="st-ring"><i class="st-jart" style="${jutsuArt(j.id)}"></i>${locked ? `<i class="st-lock">${icon('lock')}</i>` : ''}${fresh ? '<i class="st-new">新</i>' : ''}<em class="st-cost">${locked ? `Lv${unlockLvFor(charId, s)}` : s.cost}</em>${mine ? `<i class="st-carried">${icon('check')}</i>` : ''}</span>
        <i class="st-jtag">術</i><b>${esc(j.name)}</b><small class="st-jsub">${esc(s.name)}</small></button>`;
    }
    // 技（戦い方が変わる専用スキル）は挿絵の四角い札。数値だけの「道」は小さな暗い丸（名前は押したときの詳細に）
    const waza = isWaza(s);
    return `<button class="st-node g-${s.group} ${waza ? 'st-wnode' : 'st-dnode'} ${isOn ? 'got' : ''} ${locked ? 'locked' : why ? 'blocked' : 'open'} ${sel.skill === s.id ? 'sel' : ''}"
      style="left:${rem(px(x))};top:${rem(py(y))}" data-skill="${esc(s.id)}" aria-pressed="${isOn}" aria-label="${esc(s.name)}・コスト${s.cost}${locked ? `・主Lv${unlockLvFor(charId, s)}で解放` : isOn ? '・装着中' : ''}" title="${esc(s.name)}：${esc(s.desc)}">
      <span class="st-ring">${icon(skillIcon(s))}${waza ? `<i class="st-wart" style="background-image:url(/art/skills/${esc(s.id)}.webp)"></i>` : ''}${locked ? `<i class="st-lock">${icon('lock')}</i>` : ''}${fresh ? '<i class="st-new">新</i>' : ''}<em class="st-cost">${locked ? `Lv${unlockLvFor(charId, s)}` : s.cost}</em></span>
      ${waza ? `<b>${esc(s.name)}</b>` : ''}</button>`;
  };
  const s = SKILL_BY_ID[sel.skill];
  const pct = Math.min(100, (used / Math.max(1, cap)) * 100);
  body.innerHTML = `
    <div class="st-bar">
      <button class="icon-btn st-back" data-back aria-label="編成へ戻る">${icon('left')}</button>
      <div class="st-char" role="group" aria-label="キャラ切替" data-swipe-char>
        <button class="icon-btn" data-charstep="-1" aria-label="前のキャラ">${icon('left')}</button>
        <span class="cs-face" style="${faceStyle(charId)}" title="${esc(CHARS[charId].name)}"></span>
        <button class="icon-btn" data-charstep="1" aria-label="次のキャラ">${icon('right')}</button>
      </div>
      <div class="st-meter" role="meter" aria-label="ポイント" aria-valuemin="0" aria-valuemax="${cap}" aria-valuenow="${used}">
        <span class="stm-v"><small>ポイント</small><b>${used}</b>/${cap}${auto ? '<em title="主Lvが上がるたびに自動で組み直します">おまかせ</em>' : ''}</span>
        <span class="meter"><i style="width:${pct}%"></i></span>
      </div>
      <button class="btn small primary st-auto" data-a="skauto">${icon('star')}おすすめ構成</button>
      <button class="icon-btn st-save" data-a="skpreset" title="スキル構成の保存枠" aria-label="スキル構成の保存枠">${icon('save')}</button>
    </div>
    <div class="st-view" aria-label="スキルツリー（ドラッグで移動・ホイールやピンチで拡大）">
      <div class="st-world" style="width:${rem(W)};height:${rem(H)}">
        <svg class="st-lines${G ? ' graph' : ''}" viewBox="0 0 ${W} ${H}" aria-hidden="true">${lines}</svg>
        ${joints}${crownFork}${lineLabels}${groupLabels}${crownTitle}
        <span class="st-core" style="left:${rem(px(0))};top:${rem(py(0))}"><i style="${faceStyle(charId)}"></i></span>
        ${L.nodes.map(nodeBtn).join('')}
      </div>
      <div class="st-zoom"><button class="icon-btn" data-zoom="1" aria-label="拡大">${icon('plus')}</button><button class="icon-btn" data-zoom="-1" aria-label="縮小"><b>−</b></button><button class="icon-btn" data-zoom="0" aria-label="全体を見る">${icon('swap')}</button></div>
      ${s ? `<aside class="st-pop sheet-card skl-detail"><button class="icon-btn st-x" data-stclose aria-label="閉じる">${icon('close')}</button>${skillDetail(p, s, cur)}</aside>` : ''}
    </div>`;
  const view = body.querySelector<HTMLElement>('.st-view')!;
  bindCamera(view, body.querySelector<HTMLElement>('.st-world')!, charId, px(0), py(0));
  // 選んだ丸が下半分なら詳細は上に出す（丸を隠さない）
  const pop = view.querySelector<HTMLElement>('.st-pop');
  const nd = view.querySelector<HTMLElement>('.st-node.sel .st-ring');
  if (pop && nd) {
    const v = view.getBoundingClientRect();
    const r = nd.getBoundingClientRect();
    pop.classList.toggle('at-top', r.top + r.height / 2 - v.top > v.height * 0.5);
  }
}

/** 枠の中のツリーを動かす：ドラッグで移動（動かしたら丸を押したことにしない）、ホイール・ピンチ・ボタンで拡大縮小。初めてはキャラを真ん中に */
function bindCamera(view: HTMLElement, world: HTMLElement, key: string, cx: number, cy: number, fitWidth = false) {
  const rs = parseFloat(getComputedStyle(document.documentElement).fontSize) / 15; // rem → 基準の px の倍率
  const ww = world.offsetWidth;
  const wh = world.offsetHeight;
  /** 全体が入る大きさ（全体ボタン） */
  const fit = () => {
    const z = Math.max(0.3, Math.min(1.1, Math.min(view.clientWidth / ww, view.clientHeight / wh) * 0.96));
    return { z, x: view.clientWidth / 2 - (cx * rs) * z, y: view.clientHeight / 2 - (cy * rs) * z };
  };
  /** 最初：キャラを真ん中に、丸と名前が読める大きさで（全体が入るならそれで） */
  const home = () => {
    const f = fit();
    // 段：横幅がちょうど入る大きさ
    const z = fitWidth ? Math.max(0.3, Math.min(1.1, (view.clientWidth / ww) * 0.98)) : Math.max(f.z, 0.8);
    return { z, x: view.clientWidth / 2 - (cx * rs) * z, y: view.clientHeight / 2 - (cy * rs) * z };
  };
  const cam = (camera[key] ??= home());
  const apply = () => (world.style.transform = `translate(${cam.x}px, ${cam.y}px) scale(${cam.z})`);
  apply();
  const zoomAt = (f: number, mx: number, my: number) => {
    const z = Math.max(0.3, Math.min(2.2, cam.z * f));
    cam.x = mx - ((mx - cam.x) * z) / cam.z;
    cam.y = my - ((my - cam.y) * z) / cam.z;
    cam.z = z;
    apply();
  };
  view.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const r = view.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top);
    },
    { passive: false },
  );
  const pts = new Map<number, { x: number; y: number }>();
  let moved = 0;
  let pinch = 0;
  view.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('.st-zoom, .st-pop')) return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.size === 1) moved = 0;
    if (pts.size === 2) {
      const [a, b] = [...pts.values()];
      pinch = Math.hypot(a.x - b.x, a.y - b.y);
    }
  });
  view.addEventListener('pointermove', (e) => {
    const prev = pts.get(e.pointerId);
    if (!prev) return;
    const nx = e.clientX;
    const ny = e.clientY;
    if (pts.size === 1) {
      cam.x += nx - prev.x;
      cam.y += ny - prev.y;
      moved += Math.abs(nx - prev.x) + Math.abs(ny - prev.y);
      if (moved > 6 && !view.hasPointerCapture(e.pointerId)) view.setPointerCapture(e.pointerId);
      apply();
    }
    pts.set(e.pointerId, { x: nx, y: ny });
    if (pts.size === 2) {
      const [a, b] = [...pts.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const r = view.getBoundingClientRect();
      if (pinch) zoomAt(d / pinch, (a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top);
      pinch = d;
      moved = 99;
    }
  });
  const up = (e: PointerEvent) => {
    pts.delete(e.pointerId);
    if (pts.size < 2) pinch = 0;
  };
  view.addEventListener('pointerup', up);
  view.addEventListener('pointercancel', up);
  // 動かしたあとの「押した」は丸の選択にしない
  view.addEventListener(
    'click',
    (e) => {
      const zb = (e.target as HTMLElement).closest<HTMLElement>('[data-zoom]');
      if (zb) {
        e.stopPropagation();
        const k = Number(zb.dataset.zoom);
        if (k === 0) Object.assign(cam, fit()), apply();
        else zoomAt(k > 0 ? 1.25 : 0.8, view.clientWidth / 2, view.clientHeight / 2);
        return;
      }
      if (moved > 6) {
        e.stopPropagation();
        e.preventDefault();
        moved = 0;
      }
    },
    true,
  );
}

/** スキルの詳細の小窓（編成の「スキル」で押したとき） */
export function skillInfoDialog(id: string) {
  const s = SKILL_BY_ID[id];
  if (!s) return;
  const where = s.charId ? `${CHARS[s.charId].name}専用・${s.line}` : `${SKILL_GROUP_LABEL[s.group]}・${s.line}`;
  const req = s.requires.map((r) => SKILL_BY_ID[r]?.name ?? r);
  openModal(
    `<div class="sk-info"><header class="ed-head"><span class="ed-icon got">${icon(skillIcon(s))}</span><div class="ed-name"><h2>${esc(s.name)}</h2><b class="ed-lv">コスト${s.cost}</b><small class="ed-br">${esc(where)}</small></div></header>
      <p class="ed-desc">${esc(s.desc)}</p>
      ${s.surge ? `<p class="ed-note">${icon('star')}妖力を伸ばすスキル</p>` : ''}
      <p class="ed-note">主Lv${unlockLvFor(charId, s)}で解放${req.length ? `・前提：${esc(req.join('・'))}` : ''}</p></div>`,
    { title: 'スキル' },
  );
}

function skillDetail(p: Profile, s: SkillNode, cur: string[]) {
  const isOn = cur.includes(s.id);
  const why = isOn ? null : blockReason(p, s, cur);
  const locked = p.level < unlockLvFor(charId, s);
  const left = skillCapacity(p).total - skillCost(cur);
  const deps = isOn ? dependentsOf(s.id, cur).filter((x) => x !== s.id) : [];
  const where = s.charId ? `${CHARS[s.charId].name}専用・${s.line}` : `共通・${SKILL_GROUP_LABEL[s.group]}・${s.line}`;
  const req = s.requires.map((r) => SKILL_BY_ID[r]?.name ?? r);
  const og = ougiOfNode(s);
  const ogNote = og ? (og.id === selectedJutsu(p, charId) ? `<p class="ed-note">${icon('check')}セット中の術「${esc(og.name)}」の奥義です</p>` : `<p class="ed-req">${icon('info')}セットした術が「${esc(og.name)}」のときに使えます</p>`) : '';
  const j = jutsuOfNode(s);
  if (j) {
    const mine = j.id === selectedJutsu(p, charId);
    const learned = isOn;
    return `<header class="ed-head jutsu-head"><span class="ed-icon st-jart big" style="${jutsuArt(j.id)}"></span><div class="ed-name"><small class="ed-br">${esc(CHARS[charId].name)}の術・${esc(s.line)}の系統</small><h2>術「${esc(j.name)}」</h2><b class="ed-lv">${esc(s.name)}・コスト${s.cost}</b></div></header>
      <p class="ed-desc">${esc(j.desc)}</p>
      <p class="ed-note">${icon('star')}奥義版：${esc(j.ougiDesc)}</p>
      <p class="ed-note">${locked ? `${icon('lock')}主Lv${unlockLvFor(charId, s)}で解放` : `主Lv${unlockLvFor(charId, s)}で解放済み`}${s.requires.length ? `・前提：${esc(s.requires.map((r) => SKILL_BY_ID[r]?.name ?? r).join('・'))}` : ''}・この系統の下のスキルで威力が上がる</p>
      ${why && !locked ? `<p class="ed-req">${icon('info')}${esc(why)}</p>` : ''}
      <div class="ed-actions jutsu-actions">
        ${learned ? `<p class="ed-note">${icon('check')}覚えています${mine ? '（セット中）' : '・術のセットは編成の「スキル」で'}</p>` : `<button class="btn primary big wide" data-a="skon" ${why ? 'disabled' : ''} title="${esc(why ?? '')}">${locked ? `Lv${unlockLvFor(charId, s)}で解放` : `覚える（コスト${s.cost}）`}</button>`}
        ${learned ? `<button class="btn small" data-a="skoff">外す</button>` : ''}
      </div>`;
  }
  return `<header class="ed-head"><span class="ed-icon ${isOn ? 'got' : ''}">${icon(skillIcon(s))}</span><div class="ed-name"><h2>${esc(s.name)}</h2><b class="ed-lv">コスト${s.cost}</b><small class="ed-br">${esc(where)}</small></div></header>
    <p class="ed-desc clamp3">${esc(s.desc)}</p>
    ${ogNote}
    ${s.surge && !og ? `<p class="ed-note">${icon('star')}妖力を伸ばすスキル</p>` : ''}
    <p class="ed-note ${!locked && !req.length ? 'ed-lvdone' : ''}">${locked ? `${icon('lock')}主Lv${unlockLvFor(charId, s)}で解放` : `主Lv${unlockLvFor(charId, s)}で解放済み`}${req.length ? `・前提：${esc(req.join('・'))}` : ''}</p>
    ${why && !locked ? `<p class="ed-req">${icon('info')}${esc(why)}</p>` : ''}
    ${deps.length ? `<p class="ed-note">外すと一緒に外れる：${esc(deps.map((d) => SKILL_BY_ID[d]?.name ?? d).join('・'))}</p>` : ''}
    <div class="ed-actions">
      <div class="mini-kv"><span>コスト<b>${s.cost}</b></span><span>残り<b class="${!isOn && left < s.cost ? 'bad' : ''}">${left}</b></span></div>
      ${
        isOn
          ? `<button class="btn big wide" data-a="skoff">外す</button>`
          : `<button class="btn primary big wide" data-a="skon" ${why ? 'disabled' : ''} title="${esc(why ?? '')}">${locked ? `Lv${unlockLvFor(charId, s)}で解放` : '装着'}</button>`
      }
    </div>`;
}

async function toggleSkill(p: Profile, equip: boolean) {
  const cur = equippedSkills(p, charId);
  const s = SKILL_BY_ID[sel.skill];
  if (!s) return;
  let next: string[];
  if (equip) {
    const why = blockReason(p, s, cur);
    if (why) return void toast(why);
    next = [...cur, s.id];
  } else {
    const drop = dependentsOf(s.id, cur);
    if (drop.length > 1 && !(await confirmDialog(`「${s.name}」を外すと、前提にしている\n${drop.filter((x) => x !== s.id).map((x) => SKILL_BY_ID[x]?.name ?? x).join('・')}\nも一緒に外れます。`, { ok: '外す' }))) return;
    next = cur.filter((x) => !drop.includes(x));
  }
  await run(api.skillsSet(charId, next));
}

/** 保存枠（無料1枠、権利 build_slots で10枠）：枠を選んで保存／呼び出し。鍵の枠はショップへ */
function presetDialog(p: Profile) {
  const n = presetSlots(p);
  const max = META_CONFIG.skills.presetSlots;
  let slot = 0;
  const html = () => {
    const pr = state.profile?.skills?.[charId]?.presets ?? [];
    const x = pr[slot];
    return `<div class="skl-preset">
      <div class="skp-grid">${Array.from({ length: max }, (_, i) => {
        const v = pr[i];
        const lock = i >= n;
        return `<button class="skp-cell ${lock ? 'lock' : v ? 'filled' : ''} ${i === slot && !lock ? 'sel' : ''}" data-slot="${i}" aria-pressed="${i === slot}">
          <b>${lock ? icon('lock') : i + 1}</b><small>${lock ? '' : v ? `コスト${skillCost(v)}` : '空き'}</small></button>`;
      }).join('')}</div>
      <div class="btn-row"><button class="btn" data-save>${icon('check')}今の構成を保存</button><button class="btn primary" data-load ${x ? '' : 'disabled'}>${icon('swap')}呼び出す</button></div>
      ${n < max ? `<button class="btn small skp-buy" data-go>${icon('lock')}${max}枠にする（ショップ）</button>` : ''}</div>`;
  };
  const m = openModal(html(), { title: `${CHARS[charId].name}の保存枠` });
  m.body.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const cell = t.closest<HTMLButtonElement>('[data-slot]');
    if (cell) {
      const i = Number(cell.dataset.slot);
      if (i >= n) {
        m.close();
        return go('shop', { cat: 'perm', focus: 'build_slots', from: 'build_slots' });
      }
      slot = i;
      m.body.innerHTML = html();
      return;
    }
    if (t.closest('[data-go]')) {
      m.close();
      return go('shop', { cat: 'perm', focus: 'build_slots', from: 'build_slots' });
    }
    const b = t.closest<HTMLButtonElement>('[data-save],[data-load]');
    if (!b) return;
    b.disabled = true;
    const ok = b.dataset.save != null ? await run(api.skillsPresetSave(charId, slot), `枠${slot + 1}に保存しました`) : await run(api.skillsPresetLoad(charId, slot), `枠${slot + 1}の構成にしました`);
    if (ok && b.dataset.load != null) m.close();
    else m.body.innerHTML = html();
  });
}

/** 術を覚えるスキル（jutsu:<術> を持つノード）なら、その術 */
/** 技：戦い方が変わる専用スキル（戦闘のフラグを持つ。術を覚える・奥義は別の形） */
const isWaza = (s: SkillNode) => !!s.charId && s.flags.length > 0 && !s.flags.some((f) => f.startsWith('jutsu:') || f.startsWith('jougi:'));

function jutsuOfNode(s: SkillNode) {
  const f = s.flags.find((x) => x.startsWith('jutsu:'));
  return f ? JUTSU_BY_ID[f.slice(6)] : undefined;
}
/** 術の絵：新しい術は術のエフェクト（fx-jutsu.webp）、基本の術は戦闘のエフェクト（fx.webp）の本体のセル */
const JUTSU_ART: Record<string, [string, number]> = {
  okuribi: ['fx-jutsu', 0], toro: ['fx-jutsu', 2], shukuchi: ['fx-jutsu', 5], tsumuji: ['fx-jutsu', 6], toppu: ['fx-jutsu', 9], shuzen: ['fx-jutsu', 11], kitsunebijin: ['fx-jutsu', 12], gekka: ['fx-jutsu', 14],
  hanabi: ['fx', 1], shippu: ['fx', 3], hajo: ['fx', 6], kitsunebigiri: ['fx', 8],
};
export function jutsuArt(id: string): string {
  const [file, i] = JUTSU_ART[id] ?? ['fx-jutsu', 0];
  return `background-image:url(/art/${file}.webp);background-size:400% 400%;background-position:${((i % 4) / 3) * 100}% ${(Math.floor(i / 4) / 3) * 100}%;`;
}
/** 系統の先の奥義のスキル（jougi:<術> を持つノード）なら、その術 */
function ougiOfNode(s: SkillNode) {
  const f = s.flags.find((x) => x.startsWith('jougi:'));
  return f ? JUTSU_BY_ID[f.slice(6)] : undefined;
}

// ---------- 段（skill-ladder）：下のキャラから上へ行が積み上がる。各行から1つ、選んだマスの真上・斜め上へ ----------
/** 半マスの幅・行の間隔（奥義の行は広く）・左の目盛りの位置（U 単位） */
const LAD = { hx: 0.82, gap: 1.45, big: 0.55, ruler: 0.55, left: 2.05, pad: 1.1 };
const isOugiNode = (s: SkillNode) => s.flags.some((f) => f.startsWith('jougi:'));

function drawLadder(body: HTMLElement, p: Profile) {
  const rows = skillLadder(charId)!;
  const cur = equippedSkills(p, charId);
  const on = new Set(cur);
  const open = ladderOpen(charId, p.level);
  const auto = p.skills?.[charId]?.auto !== false;
  // 行の高さ（下から）。奥義の行の上下は広く
  const ys: number[] = [];
  let y = 0;
  rows.forEach((r, k) => {
    const big = r.ids.some((id) => SKILL_BY_ID[id] && isOugiNode(SKILL_BY_ID[id]));
    const prevBig = k > 0 && rows[k - 1].ids.some((id) => SKILL_BY_ID[id] && isOugiNode(SKILL_BY_ID[id]));
    y -= k === 0 ? 0 : LAD.gap + (big || prevBig ? LAD.big : 0);
    ys.push(y);
  });
  const coreY = 1.5;
  const top = ys[ys.length - 1] - 1.4;
  const W = (LAD.left + 6 * LAD.hx + 1.05) * U;
  const H = (coreY + 1.0 - top) * U;
  const px = (x: number) => x * U;
  const py = (yy: number) => (yy - top) * U;
  const nx = (n: number, i: number) => LAD.left + ladderX(n, i) * LAD.hx;
  const cx = nx(1, 0);
  // 主Lv → 高さ（行の間は等分。最初の行より下は中心へ）
  const lvY = (lv: number) => {
    if (lv <= rows[0].lv) return ys[0];
    for (let k = 1; k < rows.length; k++) if (lv <= rows[k].lv) return ys[k - 1] + ((ys[k] - ys[k - 1]) * (lv - rows[k - 1].lv)) / (rows[k].lv - rows[k - 1].lv);
    return ys[ys.length - 1];
  };
  const chosenI = rows.map((r) => r.ids.findIndex((id) => on.has(id)));
  let lines = '';
  const seg = (ax: number, ay: number, bx: number, by: number, cls: string) => `<line x1="${px(ax)}" y1="${py(ay)}" x2="${px(bx)}" y2="${py(by)}" class="${cls}"/>`;
  // 中心 → 最初の行
  rows[0].ids.forEach((_, j) => (lines += seg(cx, coreY, nx(rows[0].ids.length, j), ys[0], `lad ${j === chosenI[0] ? 'on' : 0 < open ? 'cand' : ''}`)));
  for (let k = 1; k < rows.length; k++) {
    const a = rows[k - 1].ids.length;
    const b = rows[k].ids.length;
    for (let i = 0; i < a; i++)
      for (let j = 0; j < b; j++) {
        if (!ladderLinked(a, i, b, j)) continue;
        const lit = i === chosenI[k - 1] && j === chosenI[k];
        const cand = !lit && i === chosenI[k - 1] && k < open;
        lines += seg(nx(a, i), ys[k - 1], nx(b, j), ys[k], `lad ${lit ? 'on' : cand ? 'cand' : k >= open ? 'shut' : ''}`);
      }
  }
  // 左の目盛り：主Lv5つごと（能力が上がる）。届いたところは金
  const rx = LAD.ruler;
  const reach = statLevel(p.level);
  lines += seg(rx, coreY, rx, lvY(rows[rows.length - 1].lv), 'ruler');
  lines += seg(rx, coreY, rx, lvY(p.level), 'ruler on');
  for (let lv = STAT_STEP; lv <= rows[rows.length - 1].lv; lv += STAT_STEP) {
    const ty = lvY(lv);
    const major = rows.some((r) => r.lv === lv);
    lines += seg(rx - (major ? 0.16 : 0.09), ty, rx + (major ? 0.16 : 0.09), ty, `tick ${lv <= reach ? 'on' : ''}`);
  }
  const labels = rows
    .map((r, k) => `<span class="lad-lv ${k < open ? 'on' : ''}" style="left:${rem(px(rx + 0.24))};top:${rem(py(ys[k]))}">Lv${r.lv}</span>`)
    .join('');
  const here = `<span class="lad-here" style="left:${rem(px(rx))};top:${rem(py(lvY(p.level)))}"></span>`;
  const carried = selectedJutsu(p, charId);
  const nodeBtn = (id: string, k: number, i: number) => {
    const s = SKILL_BY_ID[id];
    const n = rows[k].ids.length;
    const locked = k >= open;
    const isOn = on.has(id);
    const linked = k === 0 || (chosenI[k - 1] >= 0 && ladderLinked(rows[k - 1].ids.length, chosenI[k - 1], n, i));
    const state = isOn ? 'got' : locked ? 'locked' : linked ? 'open' : 'blocked';
    const fresh = !locked && rows[k].lv > newSince && rows[k].lv > 1;
    const style = `left:${rem(px(nx(n, i)))};top:${rem(py(ys[k]))}`;
    const extra = `${locked ? `<i class="st-lock">${icon('lock')}</i>` : ''}${fresh ? '<i class="st-new">新</i>' : ''}`;
    const cls = `st-node lad-node ${state} ${sel.skill === id ? 'sel' : ''}`;
    const aria = `aria-pressed="${isOn}" aria-label="${esc(s.name)}${locked ? `・主Lv${rows[k].lv}で解放` : isOn ? '・選択中' : ''}"`;
    const og = ougiOfNode(s);
    if (og)
      return `<button class="${cls} st-onode ${og.id === carried ? 'carried' : ''}" style="${style}" data-skill="${esc(id)}" ${aria}>
        <span class="st-ring"><i class="st-oart" style="${jutsuArt(og.id)}"></i>${extra}</span><b>${esc(og.ougiName)}</b></button>`;
    const j = jutsuOfNode(s);
    if (j)
      return `<button class="${cls} st-jnode" style="${style}" data-skill="${esc(id)}" ${aria}>
        <span class="st-ring"><i class="st-jart" style="${jutsuArt(j.id)}"></i>${extra}</span><b>${esc(j.name)}</b></button>`;
    if (isWaza(s) || (s.charId && !/_lv\d+_/.test(id)))
      return `<button class="${cls} g-${s.group} st-wnode" style="${style}" data-skill="${esc(id)}" ${aria}>
        <span class="st-ring">${icon(skillIcon(s))}<i class="st-wart" style="background-image:url(/art/skills/${esc(id)}.webp)"></i>${extra}</span><b>${esc(s.name)}</b></button>`;
    // 能力・共通の小技：小さな丸と短い効果
    const short = /_lv\d+_/.test(id) ? effectText(s.effects[0]) : s.name;
    return `<button class="${cls} g-${s.group} st-dnode" style="${style}" data-skill="${esc(id)}" ${aria}>
      <span class="st-ring">${icon(skillIcon(s))}${extra}</span><small class="lad-v">${esc(short)}</small></button>`;
  };
  const nodes = rows.map((r, k) => r.ids.map((id, i) => (SKILL_BY_ID[id] ? nodeBtn(id, k, i) : '')).join('')).join('');
  if (sel.skill && !rows.some((r) => r.ids.includes(sel.skill))) sel.skill = '';
  const s = SKILL_BY_ID[sel.skill];
  const next = rows[open]?.lv;
  body.innerHTML = `
    <div class="st-bar">
      <button class="icon-btn st-back" data-back aria-label="編成へ戻る">${icon('left')}</button>
      <div class="st-char" role="group" aria-label="キャラ切替" data-swipe-char>
        <button class="icon-btn" data-charstep="-1" aria-label="前のキャラ">${icon('left')}</button>
        <span class="cs-face" style="${faceStyle(charId)}" title="${esc(CHARS[charId].name)}"></span>
        <button class="icon-btn" data-charstep="1" aria-label="次のキャラ">${icon('right')}</button>
      </div>
      <div class="st-meter lad-meter">
        <span class="stm-v"><small>次の行</small><b>${next ? `Lv${next}` : '—'}</b>${auto ? '<em title="主Lvが上がるたびに自動で組み直します">おまかせ</em>' : ''}</span>
      </div>
      <button class="btn small primary st-auto" data-a="skauto">${icon('star')}おすすめ構成</button>
      <button class="icon-btn st-save" data-a="skpreset" title="スキル構成の保存枠" aria-label="スキル構成の保存枠">${icon('save')}</button>
    </div>
    <div class="st-view lad-view" aria-label="スキルの段（ドラッグで移動・ホイールやピンチで拡大）">
      <div class="st-world" style="width:${rem(W)};height:${rem(H)}">
        <svg class="st-lines lad-lines" viewBox="0 0 ${W} ${H}" aria-hidden="true">${lines}</svg>
        ${labels}${here}
        <span class="st-core lad-core" style="left:${rem(px(cx))};top:${rem(py(coreY))}"><i style="${faceStyle(charId)}"></i></span>
        ${nodes}
      </div>
      <div class="st-zoom"><button class="icon-btn" data-zoom="1" aria-label="拡大">${icon('plus')}</button><button class="icon-btn" data-zoom="-1" aria-label="縮小"><b>−</b></button><button class="icon-btn" data-zoom="0" aria-label="全体を見る">${icon('swap')}</button></div>
      ${s ? `<aside class="st-pop sheet-card skl-detail"><button class="icon-btn st-x" data-stclose aria-label="閉じる">${icon('close')}</button>${ladderDetail(p, s, cur)}</aside>` : ''}
    </div>`;
  const view = body.querySelector<HTMLElement>('.st-view')!;
  // 最初は、いちばん上の開いた行が真ん中に来るように
  bindCamera(view, body.querySelector<HTMLElement>('.st-world')!, `${charId}:ladder`, W / 2, py(ys[Math.max(0, open - 1)] - 0.3), true);
  const pop = view.querySelector<HTMLElement>('.st-pop');
  const nd = view.querySelector<HTMLElement>('.st-node.sel .st-ring');
  if (pop && nd) {
    const v = view.getBoundingClientRect();
    const r = nd.getBoundingClientRect();
    pop.classList.toggle('at-top', r.top + r.height / 2 - v.top > v.height * 0.5);
  }
}

function ladderDetail(p: Profile, s: SkillNode, cur: string[]) {
  const at = ladderAt(charId, s.id)!;
  const lv = skillLadder(charId)![at.row].lv;
  const locked = p.level < lv;
  const isOn = cur.includes(s.id);
  const og = ougiOfNode(s);
  const j = jutsuOfNode(s);
  const changes = isOn || locked ? [] : ladderChanges(p, charId, cur, s.id);
  const name = og ? `奥義・${og.ougiName}` : j ? `術「${j.name}」` : s.name;
  const desc = og ? `${og.name}の奥義。${og.ougiDesc}` : j ? `${j.desc}` : s.desc;
  const art = og ? `<span class="ed-icon st-oart big" style="${jutsuArt(og.id)}"></span>` : j ? `<span class="ed-icon st-jart big" style="${jutsuArt(j.id)}"></span>` : `<span class="ed-icon ${isOn ? 'got' : ''}">${icon(skillIcon(s))}</span>`;
  return `<header class="ed-head ${og || j ? 'jutsu-head' : ''}">${art}<div class="ed-name"><h2>${esc(name)}</h2>${(j || og ? s.name : s.line) !== name ? `<small class="ed-br">${esc(j || og ? s.name : s.line)}</small>` : ''}</div></header>
    <p class="ed-desc clamp3">${esc(desc)}</p>
    ${j && !og ? `<p class="ed-note">${icon('star')}${esc(s.desc)}</p>` : ''}
    <div class="ed-actions">
      ${
        isOn
          ? `<p class="ed-note">${icon('check')}選んでいます</p>`
          : locked
            ? `<button class="btn big wide" disabled>${icon('lock')}Lv${lv}で解放</button>`
            : `<button class="btn primary big wide" data-a="skpick">${changes.length > 1 ? `道を変えてこれにする（${changes.length}行）` : 'これにする'}</button>`
      }
    </div>`;
}

async function pickLadder(p: Profile) {
  const s = SKILL_BY_ID[sel.skill];
  if (!s) return;
  const next = ladderPick(p, charId, equippedSkills(p, charId), s.id);
  if (!next) return;
  const ok = await run(api.skillsSet(charId, next));
  // 奥義を選んだら、その術を持っていく
  const og = ougiOfNode(s);
  if (ok && og && og.id !== selectedJutsu(state.profile ?? p, charId)) await run(api.skillsJutsu(charId, og.id));
}

// ---------- 系統（skill-lines）：系統ごとに Lv をポイントで上げ（＋・−）、技を1つ選ぶ。下に術（持っていく1つ） ----------
/** 能力値の帯：振り方で変わる能力。HP・攻撃・防御・速さ＋そのキャラの系統で伸びるもの2つ（花火・狐火のように能力値の上がらない系統でも、Lv を上げたら数字が動くように） */
type LineStat = [IconName, string, (lo: BattleLoadout) => number, boolean];
const BASE_STATS: LineStat[] = [
  ['heart', 'HP', (lo) => lo.stats.hp, false],
  ['attack', '攻撃', (lo) => lo.stats.atk, false],
  ['shield', '防御', (lo) => lo.stats.def, false],
  ['move', '速さ', (lo) => lo.stats.agi, false],
];
const EXTRA_STATS: Record<string, LineStat> = {
  skill: ['skill', '術', (lo) => Math.round((lo.skill.power - 1) * 100), true],
  territory: ['flag', '陣地', (lo) => Math.round(lo.territoryBonus * 1000) / 10, true],
  neutral: ['feather', '妖へ', (lo) => Math.round((lo.mult.neutral - 1) * 100), true],
  core: ['castle', '本陣', (lo) => Math.round((lo.mult.core - 1) * 100), true],
  build: ['village', '建物', (lo) => Math.round((lo.build.powerMult - 1) * 100), true],
};
const EXTRA_BY_CHAR: Record<CharId, string[]> = { akari: ['skill', 'territory'], kazamaru: ['skill', 'neutral'], suzune: ['core', 'build'], yuzuha: ['skill', 'neutral'] };
/** 系統の詳細の「次の Lv」：変わるものを全部（術の範囲も） */
const DETAIL_STATS: LineStat[] = [...BASE_STATS, ...Object.values(EXTRA_STATS), ['burst', '術の範囲', (lo) => Math.round(lo.skill.splashBonus * 1000) / 10, true]];
const lineStats = (c: CharId): LineStat[] => [...BASE_STATS, ...(EXTRA_BY_CHAR[c] ?? ['core', 'build']).map((k) => EXTRA_STATS[k])];
/** 系統の紋（アイコン） */
const LINE_ICON: Record<string, IconName> = { fushin: 'build', kojo: 'castle', mamori: 'shield', kakusei: 'star', bakuto: 'dice', hanabi: 'burst', jintori: 'flag', sokko: 'speed', ridatsu: 'foot', kari: 'feather', geigeki: 'guard', kitsunebi: 'skill', kettou: 'attack' };
/** 前に描いた能力値（振ったときに増えた分を出す） */
let lastStats: { c: CharId; v: number[] } | null = null;
/** 構成を ids にしたときの loadout（詳細の「次の Lv」の比べ用） */
const loadoutWith = (p: Profile, ids: string[]) => buildLoadout({ ...p, skills: { ...p.skills, [charId]: { ...(p.skills?.[charId] ?? { presets: [null, null] }), set: ids, auto: false } } } as Profile, charId);
/** 小数は1桁まで（陣地 +2.3% など。引き算の誤差で細かい桁が出ないように） */
const r1 = (v: number) => Math.round(v * 10) / 10;
const statText = (v: number, pct: boolean) => (pct ? `+${r1(v)}%` : r1(v).toLocaleString());

/**
 * 系統のアイコン（道具・お守りと同じ絵柄、背景は透明）。キャラごとに1枚の画像（6×4）にまとめて読む（art-src/build-skill-atlas.sh）。
 * 並び：系統の技（系統の順に Lv2・5・8・10）→ 術（列の順）。id は技のスキルID か jutsu_<術>
 */
const ATLAS = { cols: 6, rows: 4 };
const atlasSrc = (c: CharId) => `/art/skills/icons-${c}.webp`;
function iconIndex(c: CharId, id: string): number {
  const waza = (skillLines(c) ?? []).flatMap((l) => l.waza);
  const i = waza.indexOf(id);
  if (i >= 0) return i;
  const js = Object.values(JUTSU_BY_ID).filter((j) => j.charId === c).sort((a, b) => a.col - b.col).map((j) => `jutsu_${j.id}`);
  const k = js.indexOf(id);
  return k >= 0 ? waza.length + k : -1;
}
const flatArt = (id: string) => {
  const i = iconIndex(charId, id);
  if (i < 0) return '';
  return `background-image:url(${atlasSrc(charId)});background-size:${ATLAS.cols * 100}% ${ATLAS.rows * 100}%;background-position:${((i % ATLAS.cols) / (ATLAS.cols - 1)) * 100}% ${(Math.floor(i / ATLAS.cols) / (ATLAS.rows - 1)) * 100}%`;
};
/** 起動後に裏で4人分のアイコンの画像を読んでおく（スキルを開いたときに待たない） */
setTimeout(() => {
  for (const c of CHAR_IDS) if (skillLines(c)) new Image().src = atlasSrc(c);
}, 1500);
/** 最後に＋−した系統（下の詳細に出す） */
let lastLine = 0;

/** 系統の画面の中身（編成の「スキル」タブにも入れる）。案24：行＝系統、列＝Lv2・5・8・10 で開く技と極み。inChara：上の帯（戻る・キャラ切替）なし */
function linesViewHtml(p: Profile, inChara: boolean): string {
  const lines = skillLines(charId)!;
  const cur = equippedSkills(p, charId);
  const lv = lineLevels(charId, cur);
  const cap = linePointsOf(p);
  const used = lv.reduce((a, n) => a + lineCost(n), 0);
  const left = cap - used;
  const auto = p.skills?.[charId]?.auto !== false;
  if (!sel.skill || !(/^@line:/.test(sel.skill) || lines.some((l) => l.waza.includes(sel.skill)))) sel.skill = `@line:${lastLine}`;
  const rows = lines
    .map((l, i) => {
      const n = lv[i];
      const cells = l.waza
        .map((id, k) => {
          const s = SKILL_BY_ID[id];
          if (!s) return '<span></span>';
          const need = WAZA_LVS[k];
          const open = n >= need;
          const kiwami = k === WAZA_LVS.length - 1;
          return `<button class="lx-cell ${open ? 'open' : 'locked'} ${kiwami ? 'kiwami' : ''} ${sel.skill === id ? 'sel' : ''}" data-skill="${esc(id)}" aria-label="${esc(s.name)}${open ? '' : `・${esc(l.name)}Lv${need}で開く`}">
            <span class="lx-icon"><i style="${flatArt(id)}"></i>${open ? '' : `<em class="lx-need">${icon('lock')}Lv${need}</em>`}</span>
            <b>${esc(s.name)}</b></button>`;
        })
        .join('');
      const seg = Array.from({ length: LINE_MAX }, (_, k) => `<i class="${k < n ? 'on' : ''}"></i>`).join('');
      return `<section class="lx-row ${sel.skill === `@line:${i}` ? 'sel' : ''}" data-line="${i}">
        <div class="lx-left">
          <button class="lx-head" data-skill="@line:${i}" aria-label="${esc(l.name)} Lv${n}/${LINE_MAX}">
            <span class="lx-crest">${icon(LINE_ICON[l.key] ?? 'star')}</span><b>${esc(l.name)}</b><span class="lx-lv">Lv<b>${n}</b>/${LINE_MAX}</span>
          </button>
          <span class="lx-seg">${seg}</span>
          <div class="lx-step">
            <button class="lx-btn" data-a="lndown" ${n > 0 ? '' : 'disabled'} aria-label="${esc(l.name)}のLvを下げる"><i class="lx-minus"></i></button>
            <button class="lx-btn up" data-a="lnup" ${n < LINE_MAX && left > 0 ? '' : 'disabled'} aria-label="${esc(l.name)}のLvを上げる">${icon('plus')}</button>
          </div>
        </div>
        ${cells}
      </section>`;
    })
    .join('');
  // 術：持っていく1つを選ぶ。系統 Lv3 で覚え、Lv7 で奥義
  const jutsuHtml = jutsuView(p, charId)
    .map((j) => {
      const li = lines.findIndex((l) => l.col === JUTSU_BY_ID[j.id].col);
      const n = li >= 0 ? lv[li] : 0;
      const ougi = n >= LINE_OUGI_LV;
      const lname = li >= 0 ? lines[li].name : '';
      return `<button class="lx-jutsu ${j.selected ? 'got' : ''} ${j.learned ? '' : 'locked'} ${ougi ? 'has-ougi' : ''} ${sel.skill === `@jutsu:${j.id}` ? 'sel' : ''}" data-skill="@jutsu:${esc(j.id)}" aria-haspopup="dialog" aria-pressed="${j.selected}" aria-label="術「${esc(j.name)}」${j.learned ? '' : `・${esc(lname)}Lv${LINE_JUTSU_LV}で覚える`}">
        <span class="lx-jicon"><i style="${flatArt(`jutsu_${j.id}`)}"></i></span>
        <span class="lx-jtxt"><b>${esc(j.name)}</b>${j.learned ? '' : `<small>${icon('lock')}${esc(lname)}Lv${LINE_JUTSU_LV}</small>`}<em class="lx-ougi ${ougi ? 'on' : ''}">${ougi ? '' : icon('lock')}奥義</em></span>
        ${j.selected ? `<i class="lx-check">${icon('check')}</i>` : ''}</button>`;
    })
    .join('');
  // ＋−の直後：変わった能力を一瞬出す
  const lo = buildLoadout(p, charId);
  const LINE_STATS = lineStats(charId);
  const vals = LINE_STATS.map(([, , f]) => f(lo));
  const prev = lastStats?.c === charId ? lastStats.v : null;
  lastStats = { c: charId, v: vals };
  const diffs = prev ? LINE_STATS.map(([ic, name, , isPct], k) => [ic, name, r1(vals[k] - prev[k]), isPct] as const).filter(([, , d]) => d) : [];
  const diffHtml = diffs.length ? `<span class="lx-diffs">${diffs.map(([ic, name, d, isPct]) => `<em class="${d > 0 ? 'up' : 'down'}">${icon(ic)}${name}${d > 0 ? '+' : ''}${d}${isPct ? '%' : ''}</em>`).join('')}</span>` : '';
  const statsHtml = LINE_STATS.map(([ic, name, , isPct], k) => `<span class="lx-st"><i>${icon(ic)}</i><small>${name}</small><b>${statText(vals[k], isPct)}</b></span>`).join('');
  const head = `<div class="lx-points ${left > 0 ? 'has' : ''}"><span><small>残り</small><b>${left}</b><small>ポイント</small>${auto ? '<em>おまかせ</em>' : ''}</span>${diffHtml}${inChara ? `<button class="btn small primary" data-a="skauto">${icon('star')}おすすめ構成</button>` : ''}</div>
    <div class="lx-stats2" aria-label="能力値">${statsHtml}</div>
    <div class="lx-cols"><span></span>${WAZA_LVS.map((x) => `<b>Lv${x}</b>`).join('')}</div>`;
  const view = `<div class="ln-view lx lx24 ${inChara ? 'in-chara' : ''}">
      ${head}
      <div class="lx-grid">${rows}</div>
      <div class="lx-info">${lineInfo(p, sel.skill, cur)}</div>
      <section class="lx-jutsus"><h3>術</h3><div class="lx-jrow">${jutsuHtml}</div></section>
    </div>`;
  if (inChara) return view;
  return `
    <div class="st-bar">
      <button class="icon-btn st-back" data-back aria-label="編成へ戻る">${icon('left')}</button>
      <div class="st-char" role="group" aria-label="キャラ切替" data-swipe-char>
        <button class="icon-btn" data-charstep="-1" aria-label="前のキャラ">${icon('left')}</button>
        <span class="cs-face" style="${faceStyle(charId)}" title="${esc(CHARS[charId].name)}"></span>
        <button class="icon-btn" data-charstep="1" aria-label="次のキャラ">${icon('right')}</button>
      </div>
      <button class="btn small primary st-auto" data-a="skauto">${icon('star')}おすすめ構成</button>
    </div>
    ${view}`;
}
function drawLines(body: HTMLElement, p: Profile) {
  body.innerHTML = linesViewHtml(p, false);
}
/** 編成の「スキル」タブ：系統のキャラは系統の画面をそのまま（キャラ切替・戦力・タブは編成のもの） */
export function drawSkillLinesIn(body: HTMLElement, p: Profile, c: CharId) {
  if (charId !== c) (sel.skill = ''), (lastStats = null);
  charId = c;
  markSkillsSeen(p.level);
  body.innerHTML = linesViewHtml(p, true);
}
/** 編成の画面で系統の操作（＋−・術・おすすめ・詳細）を受ける */
export function bindSkillLines(el: HTMLElement, redraw: () => void): () => void {
  const h = makeHandler(redraw);
  const f = (e: Event) => {
    if ((e.target as HTMLElement).closest('.ln-view')) void h(e);
  };
  el.addEventListener('click', f);
  return () => el.removeEventListener('click', f);
}

/** 術の小窓：術の説明・奥義・セットする（系統 Lv3 で覚え、Lv7 で奥義） */
function jutsuDialog(p: Profile, jid: string) {
  const html = () => {
    const q = state.profile ?? p;
    const lines = skillLines(charId)!;
    const lv = lineLevels(charId, equippedSkills(q, charId));
    const j = jutsuView(q, charId).find((x) => x.id === jid);
    if (!j) return '';
    const li = lines.findIndex((l) => l.col === JUTSU_BY_ID[j.id].col);
    const n = li >= 0 ? lv[li] : 0;
    const lname = li >= 0 ? lines[li].name : '';
    const og = JUTSU_BY_ID[j.id];
    return `<div class="lx-jdlg">
      <header><span class="lx-iicon ${j.learned ? '' : 'locked'}"><i style="${flatArt(`jutsu_${j.id}`)}"></i></span><div><h3>${esc(j.name)}</h3><small>${lname ? `${esc(lname)}の術・${esc(lname)}Lv${n}` : '最初から覚えている術'}</small></div></header>
      <div class="lx-jbody">
        <p>${esc(j.desc)}</p>
        <p class="ougi"><b>奥義「${esc(og.ougiName)}」</b>${esc(j.ougiDesc)}<br><small>${n >= LINE_OUGI_LV ? '覚えています（妖力が参のとき、この術の代わりに放てる）' : `${esc(lname)}Lv${LINE_OUGI_LV}で覚える`}</small></p>
      </div>
      <div class="lx-jact">${j.selected ? `<span class="lx-tag on">${icon('check')}セット中</span>` : j.learned ? `<button class="btn primary big wide" data-jset>セットする</button>` : `<button class="btn big wide" disabled>${icon('lock')}${esc(lname)}Lv${LINE_JUTSU_LV}で覚える</button>`}</div>
    </div>`;
  };
  const m = openModal(html(), { title: '術' });
  m.body.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-jset]');
    if (!b) return;
    b.disabled = true;
    if (await run(api.skillsJutsu(charId, jid))) m.close();
    else if (b.isConnected) b.disabled = false;
  });
}

/** 下の詳細：系統（今の能力・次の Lv・次に開く技）／技・極み（効果と開く Lv） */
function lineInfo(p: Profile, id: string, cur: string[]) {
  const lines = skillLines(charId)!;
  const lv = lineLevels(charId, cur);
  const m = /^@line:(\d+)$/.exec(id);
  if (m) {
    const i = Number(m[1]);
    const l = lines[i];
    if (!l) return '';
    const n = lv[i];
    // 次の Lv の能力値（ポイントを1つ足した仮の主Lvで、今と1つ上げた構成を比べ、その差を今の数字に足す）
    const q = { ...p, level: p.level + 40 } as Profile;
    const up = n < LINE_MAX ? lineStep(q, charId, cur, i, 1) : null;
    const a = buildLoadout(p, charId);
    const a2 = loadoutWith(q, cur);
    const b2 = up ? loadoutWith(q, up) : null;
    const diff = b2 ? DETAIL_STATS.map(([ic, name, f, isPct]) => [ic, name, r1(f(a)), r1(f(a) + f(b2) - f(a2)), isPct] as const).filter(([, , x, y]) => x !== y) : [];
    const k = WAZA_LVS.findIndex((x) => x > n);
    const nextWaza = k >= 0 ? SKILL_BY_ID[l.waza[k]] : null;
    return `<span class="lx-iicon crest">${icon(LINE_ICON[l.key] ?? 'star')}</span>
      <div class="lx-itxt"><h4>${esc(l.name)}<small>Lv${n}/${LINE_MAX}</small></h4>
      ${diff.length ? `<p class="lx-next"><small>Lv${n + 1}</small>${diff.map(([ic, name, x, y, isPct]) => `<span>${icon(ic)}${name} ${statText(x, isPct)}<i>→</i><b>${statText(y, isPct)}</b></span>`).join('')}</p>` : `<p>${n >= LINE_MAX ? '極みに達しました' : ''}</p>`}
      ${nextWaza ? `<p class="sub">Lv${WAZA_LVS[k]}で「${esc(nextWaza.name)}」</p>` : ''}</div>`;
  }
  const s = SKILL_BY_ID[id];
  const w = s && lineOfWaza(charId, id);
  if (!s || !w) return '';
  const open = lv[w.line] >= w.need;
  return `<span class="lx-iicon ${open ? '' : 'locked'}"><i style="${flatArt(id)}"></i></span>
    <div class="lx-itxt"><h4>${esc(s.name)}<small>${esc(lines[w.line].name)}${w.kiwami ? 'の極み' : ''}</small></h4><p>${esc(s.desc.replace(/^(普請|攻城|守り|覚醒|博徒)の極み・?/, ''))}</p></div>
    ${open ? '' : `<div class="lx-iact"><span class="lx-tag">${icon('lock')}Lv${w.need}で開く</span></div>`}`;
}
